import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  AlertCircle,
  FileText,
  Loader2,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { useRef, useState } from 'react';

import { GET_KB_DOCUMENT_UPLOAD_URL_QUERY } from '@/apollo/schemas/knowledgeBase/getKbDocumentUploadUrl';
import { LIST_KB_DOCUMENTS_QUERY } from '@/apollo/schemas/knowledgeBase/listKbDocuments';
import { TRIGGER_KB_DOCUMENT_SYNC } from '@/apollo/schemas/knowledgeBase/triggerKbDocumentSync';
import { AdminActionWrapper } from '@/components/common/AdminActionWrapper';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useKbAuth } from '@/hooks/useKbAuth';
import { IKbDocument } from '@/types/knowledgeBase.types';
import {
  formatFileSize,
  formatKbDate,
  isKbOk,
  kbErrorMessage,
} from '@/utils/knowledgeBase';

const KbDocuments = () => {
  const { toast } = useToast();
  const { identityToken, kbContext } = useKbAuth();
  const client = useApolloClient();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const { data, loading, error, refetch } = useQuery(LIST_KB_DOCUMENTS_QUERY, {
    skip: !identityToken,
    fetchPolicy: 'cache-and-network',
    context: kbContext(),
  });

  const payload = data?.listKbDocuments;
  const documents: IKbDocument[] = payload?.documents ?? [];

  const loadError = error
    ? error.message
    : payload && !isKbOk(payload)
      ? kbErrorMessage(payload, 'Could not load documents.')
      : null;

  const [triggerSync, { loading: syncing }] = useMutation(
    TRIGGER_KB_DOCUMENT_SYNC,
  );

  const handleUpload = async (file: File) => {
    setUploading(true);

    try {
      // A Query, not a mutation — it mints the presigned PUT.
      const { data: urlData } = await client.query({
        query: GET_KB_DOCUMENT_UPLOAD_URL_QUERY,
        fetchPolicy: 'network-only',
        variables: {
          input: { fileName: file.name, contentType: file.type },
        },
        context: kbContext(),
      });

      const result = urlData?.getKbDocumentUploadUrl;
      if (!isKbOk(result) || !result?.uploadUrl) {
        toast({
          variant: 'destructive',
          title: 'Could not start upload',
          description: kbErrorMessage(result, 'No upload URL was returned.'),
        });
        return;
      }

      const uploadResponse = await fetch(result.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': result.contentType || file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        toast({
          variant: 'destructive',
          title: 'Upload failed',
          description: `Storage rejected the file (${uploadResponse.status}). The upload link may have expired — try again.`,
        });
        return;
      }

      toast({
        variant: 'success',
        title: 'Document uploaded',
        description: `${file.name} is stored. Run "Sync documents" to index it.`,
      });
      await refetch();
    } catch (uploadError) {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: (uploadError as Error).message,
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTriggerSync = async () => {
    try {
      const { data: syncData } = await triggerSync({ context: kbContext() });

      const result = syncData?.triggerKbDocumentSync;
      if (!isKbOk(result)) {
        toast({
          variant: 'destructive',
          title: 'Could not start document sync',
          description: kbErrorMessage(result),
        });
        return;
      }

      toast({
        variant: 'success',
        title: 'Document sync started',
        description:
          result.message ||
          'Indexing runs in the background — new documents become searchable once it finishes.',
      });
    } catch (syncError) {
      toast({
        variant: 'destructive',
        title: 'Could not start document sync',
        description: (syncError as Error).message,
      });
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-muted-foreground">
          {loading && !documents.length
            ? 'Loading documents…'
            : `${documents.length} document${documents.length === 1 ? '' : 's'}`}
        </p>

        <div className="flex items-center gap-2">
          <AdminActionWrapper>
            <Button
              variant="outline"
              onClick={handleTriggerSync}
              disabled={syncing}
            >
              {syncing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync documents
            </Button>
          </AdminActionWrapper>

          <AdminActionWrapper>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {uploading ? 'Uploading…' : 'Upload document'}
            </Button>
          </AdminActionWrapper>

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.txt,.md,.doc,.docx,.csv,.html"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) handleUpload(file);
            }}
          />
        </div>
      </div>

      {loadError && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 mb-4">
          <AlertCircle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-destructive">
              Could not load documents
            </p>
            <p className="text-muted-foreground">{loadError}</p>
          </div>
        </div>
      )}

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead className="w-[120px]">Size</TableHead>
              <TableHead className="w-[180px]">Last modified</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!documents.length && !loading && !loadError && (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="h-24 text-center text-muted-foreground"
                >
                  No documents uploaded yet.
                </TableCell>
              </TableRow>
            )}

            {documents.map((document) => (
              <TableRow key={document.s3Key}>
                <TableCell>
                  <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="break-all">{document.fileName}</p>
                      <p className="text-xs text-muted-foreground break-all">
                        {document.s3Key}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatFileSize(document.size)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatKbDate(document.lastModified)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default KbDocuments;
